<script lang="ts">
	import type { SearchMethodParams, SearchResponse } from "algoliasearch";
	import type { Hit, LiteClient } from "algoliasearch/lite";
	import type { ClassValue } from "svelte/elements";
	import industryIcon from "@core/assets/icons/industry.svg?raw";
	import logisticsIcon from "@core/assets/icons/logistics.svg?raw";
	import postIcon from "@core/assets/icons/post.svg?raw";
	import productIcon from "@core/assets/icons/product.svg?raw";
	import type { Indexable } from "@core/schema/Indexable.js";
	import { NewsKind } from "@core/schema/NewsKind.js";
	import type { IndexableTable } from "@core/schema/indexableTables.js";
	import { getIndexableName } from "@core/schema/names/getIndexableName.js";
	import { getNewsDetailPath } from "@core/schema/paths/getNewsDetailPath.js";
	import { getPartnersGoldDetailPath } from "@core/schema/paths/getPartnersGoldDetailPath.js";
	import { getProductsCatalogueDetailPath } from "@core/schema/paths/getProductsCatalogueDetailPath.js";
	import { getProductsCataloguePath } from "@core/schema/paths/getProductsCataloguePath.js";
	import { getServicesSolutionsDetailPath } from "@core/schema/paths/getServicesSolutionsDetailPath.js";
	import { getAlgoliaIndexName } from "@core/schema/utils/getAlgoliaIndexName.js";
	import { createPromiseWithStatus } from "@core/utils/svelte/createPromiseWithStatus.svelte.js";
	import { tw } from "@core/utils/svelte/tw.js";
	import { getGlobalSettings } from "../contexts/getGlobalSettings.js";
	import { getGlobalTemplateProps } from "../contexts/getGlobalTemplateProps.js";
	import Formatted from "./common/Formatted.svelte";
	import Icon from "./common/Icon.svelte";

	const { searchText }: { searchText: string } = $props();

	const iconMap: Record<IndexableTable, string> = {
		company: industryIcon,
		news: postIcon,
		product: productIcon,
		solution: logisticsIcon,
		product_category: productIcon,
	};

	const tagClassGetterMap: { [K in IndexableTable]: (hit: Hit<Indexable<K>>) => ClassValue } = {
		company: () => tw("bg-[#30B0C7]"),
		news: ({ _data: { kind } }) =>
			({
				[NewsKind.Article]: tw("bg-[#007AFF]"),
				[NewsKind.OneTimeArticle]: tw("bg-[#007AFF]"),
				[NewsKind.CaseStudy]: tw("bg-[#34C759]"),
				[NewsKind.Comparison]: tw("bg-[#007AFF]"),
			})[kind],
		product: () => tw("bg-[#FF3B30]"),
		solution: () => tw("bg-[#30B0C7]"),
		product_category: () => tw("bg-[#FF3B30]"),
	};

	const { tenant } = getGlobalTemplateProps();
	const detailPathGetterMap: { [K in IndexableTable]: (hit: Hit<Indexable<K>>) => string } = {
		company: (hit) => getPartnersGoldDetailPath(tenant, { name: hit.name, urlSlug: hit._data.urlSlug }),
		news: (hit) => getNewsDetailPath({ kind: hit._data.kind, locale: tenant.locale, urlSlug: hit._data.urlSlug }),
		product: (hit) => getProductsCatalogueDetailPath({ locale: tenant.locale, urlSlug: hit._data.urlSlug }),
		solution: (hit) => getServicesSolutionsDetailPath(tenant, { urlSlug: hit._data.urlSlug }),
		product_category: (hit) => getProductsCataloguePath(tenant.locale, [{ id: hit.objectID, name: hit.name }]),
	};

	let nextSearch = "";
	let canMakeRequest = true;
	let results = $state.raw<SearchResponse<Indexable>[]>([]);
	let client: LiteClient | undefined;

	const { ALGOLIA_API_SEARCH_KEY, ALGOLIA_APPLICATION_ID, PROJECT, ENV } = getGlobalSettings();

	const search = createPromiseWithStatus(async (params: SearchMethodParams) => {
		if (!client) {
			const { liteClient } = await import("algoliasearch/lite");
			// eslint-disable-next-line require-atomic-updates
			client = liteClient(ALGOLIA_APPLICATION_ID, ALGOLIA_API_SEARCH_KEY);
		}
		return client.searchForHits<Indexable>(params);
	});

	async function onSearchTextChange(text: string): Promise<void> {
		nextSearch = text;
		if (!canMakeRequest) {
			return;
		}

		if (text.length <= 2) {
			results = [];
			return;
		}

		try {
			canMakeRequest = false;
			({ results } = await search.call({
				requests: [
					{
						indexName: getAlgoliaIndexName(ENV, PROJECT, tenant.locale),
						query: text,
					},
				],
			}));
		} finally {
			window.setTimeout(() => {
				canMakeRequest = true;
				if (nextSearch !== text) {
					void onSearchTextChange(nextSearch);
				}
			}, 250);
		}
	}

	function getHighlightedText<T extends Indexable>(hit: T, key: Extract<keyof T, string>): string {
		const { _highlightResult } = hit;
		if (!_highlightResult) {
			return "";
		}
		const property = _highlightResult[key];
		if (!property || !("value" in property) || typeof property.value !== "string") {
			return "";
		}

		return property.value;
	}

	$effect(() => {
		void onSearchTextChange(searchText);
	});

	const hits = $derived(results.flatMap(({ hits }) => hits));

	/* eslint-disable svelte/no-at-html-tags */
</script>

<div class="flex flex-col *:bg-white">
	{#if searchText.length <= 2}
		<div class="flex p-2 items-center border-b-[1.5px] border-b-cmclightgray h-16 overflow-hidden">
			<div class="text-xs/tight grow truncate tracking-[0.02em]">
				<Formatted text="Výsledky se zobrazí zde…" />
			</div>
		</div>
	{:else if hits.length === 0}
		<div class="flex p-2 items-center border-b-[1.5px] border-b-cmclightgray h-16 overflow-hidden">
			<div class="text-xs/tight grow truncate tracking-[0.02em]">
				<Formatted text="Váš dotaz neodpovídá žádnému ze záznamů." />
			</div>
		</div>
	{:else}
		{#each hits as hit (hit.objectID)}
			{@const [tag] = hit._tags}
			<a
				class={[
					"flex p-2 gap-2 items-center border-b-[1.5px] hover:bg-cmclightgray",
					" border-b-cmclightgray overflow-hidden",
					/* @ts-expect-error hit is dynamically typed */
				]}
				href={detailPathGetterMap[tag](hit)}
			>
				{#if hit.image}
					<img
						src={hit.image}
						alt={"title" in hit ? hit.title : hit.name}
						width={48}
						height={48}
						class="rounded size-12 shrink-0 border object-contain"
					/>
				{:else}
					<div class="size-12 flex justify-center items-center shrink-0">
						<Icon icon={iconMap[tag]} class="size-8 text-cmcmidgray" />
					</div>
				{/if}
				<div class="text-xs/tight [&_em]:font-semibold [&_em]:not-italic grow truncate tracking-[0.02em]">
					{#if "title" in hit}
						{@html getHighlightedText(hit, "title")}
					{:else}
						{@html getHighlightedText(hit, "name")}
					{/if}
				</div>
				<div
					class={[
						"shrink-0 mx-2 text-2xs/none font-semibold rounded py-1 px-2 text-white",
						/* @ts-expect-error hit is dynamically typed */
						tagClassGetterMap[tag](hit),
					]}
				>
					{getIndexableName(hit)[tenant.locale]}
				</div>
			</a>
		{/each}
	{/if}
</div>
